import { FC, useMemo } from 'react';
import { BorderedContainer } from 'core/components/common/BorderedContainer';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { formatDateTime } from 'core/components/common/converters';
import {
  IncidentDetailForSendai,
  PatientTimeSeriesForSendai,
} from '../useIncidentDetailForSendai';

type Props = {
  incidentInfo: IncidentDetailForSendai;
  patientTimeSeries?: PatientTimeSeriesForSendai;
};

const formatTime = (value?: string | null) => formatDateTime(value ?? '');

const formatWorkDuration = (start?: string | null, end?: string | null) => {
  if (!start || !end) return '-- 時間 -- 分';
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '-- 時間 -- 分';
  }
  if (endDate.getTime() < startDate.getTime()) {
    return '0 時間 0 分';
  }
  const diffMs = endDate.getTime() - startDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return `${hours} 時間 ${minutes} 分`;
};

export const IncidentInformationView: FC<Props> = ({
  incidentInfo,
  patientTimeSeries,
}) => {
  const loadData = incidentInfo.loadData;

  const timeSeries = useMemo(
    () => [
      { title: '入電', content: formatTime(incidentInfo.callReceivedAt) },
      { title: '指令', content: formatTime(incidentInfo.telegramAt) },
      { title: '出場', content: formatTime(incidentInfo.departureAt) },
      { title: '現場到着', content: formatTime(incidentInfo.siteArrivalAt) },
      {
        title: '接触',
        content: formatTime(patientTimeSeries?.patientContactAt),
      },
      {
        title: '搬出開始',
        content: formatTime(patientTimeSeries?.extricationStartAt),
      },
      {
        title: '車内収容',
        content: formatTime(patientTimeSeries?.ambulanceAccommodationAt),
      },
      {
        title: '現場出発',
        content: formatTime(patientTimeSeries?.siteDepartureAt),
      },
      {
        title: '病院到着',
        content: formatTime(patientTimeSeries?.hospitalArrivalAt),
      },
      {
        title: '病院収容',
        content: formatTime(patientTimeSeries?.hospitalAccommodationAt),
      },
      { title: '引揚', content: formatTime(incidentInfo.hospitalDepartureAt) },
      { title: '帰署', content: formatTime(incidentInfo.returnAt) },
      {
        title: '従事時間',
        content: formatWorkDuration(
          incidentInfo.telegramAt,
          incidentInfo.returnAt
        ),
      },
    ],
    [incidentInfo, patientTimeSeries]
  );

  return (
    <div className="flex flex-col gap-[16px]">
      <SectionTitle text="事案情報" />
      <BorderedContainer>
        <div className="flex flex-col gap-[24px]">
          {/* 災害発生番号、事故種別、係別 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            <InfoDisplay
              title="災害発生番号"
              content={incidentInfo.incidentNo}
            />
            <InfoDisplay title="事故種別" content={incidentInfo.incidentType} />
            <InfoDisplay title="係別" content={incidentInfo.incidentSection} />
          </div>

          {/* 出場先住所、地下/地上、階数 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            <InfoDisplay
              title="出場先住所"
              content={incidentInfo.telegramTargetAddress ?? ''}
            />
            <InfoDisplay
              title="地下/地上"
              content={incidentInfo.telegramTargetAddressType ?? ''}
            />
            <InfoDisplay
              title="階数"
              content={
                incidentInfo.targetAddressFloors !== null &&
                incidentInfo.targetAddressFloors !== undefined
                  ? String(incidentInfo.targetAddressFloors)
                  : ''
              }
            />
          </div>

          {/* 発生場所 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            <InfoDisplay
              title="発生場所"
              content={incidentInfo.occurrenceAddress ?? ''}
            />
          </div>

          {/* 活動区分 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            <InfoDisplay
              title="活動区分"
              content={incidentInfo.activityType ?? ''}
            />
          </div>

          {/* 東北自動車道等 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            <InfoDisplay
              title="東北自動車道等（道路種別）"
              content={loadData?.loadType ?? ''}
            />
            <InfoDisplay
              title="東北自動車道等 上下"
              content={loadData?.loadDirection ?? ''}
            />
            <InfoDisplay
              title="東北自動車道等 KP"
              content={loadData?.kpNum ?? ''}
            />
            <InfoDisplay
              title="東北自動車道等 PA等の名称"
              content={loadData?.paText ?? ''}
            />
          </div>

          {/* 受令場所、医師現場出場 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            <InfoDisplay
              title="受令場所"
              content={incidentInfo.dispatchStatus ?? ''}
            />
            <InfoDisplay
              title="医師現場出場・その他医師"
              content={incidentInfo.doctorsOnDispatch?.[0]?.name ?? ''}
            />
          </div>

          {/* 時系列 */}
          <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
            {timeSeries.map(item => (
              <InfoDisplay
                key={item.title}
                title={item.title}
                content={item.content}
              />
            ))}
          </div>
        </div>
      </BorderedContainer>
    </div>
  );
};
